import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ContractCgvStatus } from '@core/models/contract-cgv-status.model';
import { ContractCgvType } from '@core/models/contract-cgv-type.model';
import { ContractSector } from '@core/models/contract-sector.model';
import { ContractState } from '@core/models/contract-state.model';
import { ContractUt } from '@core/models/contract-ut.model';
import { ContractCgvTypeService } from '@core/services/contract-cgv-type/contract-cgv-type.service';
import { ContractStateService } from '@core/services/contract-state/contract-state.service';
import { ContractUtService } from '@core/services/contract-ut/contract-ut.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-contract-informations',
  templateUrl: './contract-informations.component.html',
  styleUrls: ['./contract-informations.component.scss'],
})
export class ContractInformationsComponent implements OnInit {
  @Input() contractItemInformations: any;

  contractFormGroup: FormGroup;

  sentOnDateForm: FormGroup;
  sentOnDate: string; // format: 'DD/MM/YYYY'
  customerReturnDateForm: FormGroup;
  customerReturnDate: string; // format: 'DD/MM/YYYY'

  // Autocomplete data
  isContractCgvTypeLoading = false;
  contractTypeList: ContractCgvType[] = [];
  filteredContractTypeList: Observable<ContractCgvType[]>;

  isContractUtLoading = false;
  salesServiceManagerList: ContractUt[] = [];
  filteredSalesServiceManagerList: Observable<ContractUt[]>;
  billingManagerList: ContractUt[] = [];
  filteredBillingManagerList: Observable<ContractUt[]>;

  isContractStateLoading = false;
  contractStateList: ContractState[] = [];
  filteredContractStateList: Observable<ContractState[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    private contractCgvTypeService: ContractCgvTypeService,
    private contractUtService: ContractUtService,
    private contractStateService: ContractStateService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initContractForm();
    this.getContractCgvType();
    this.getContractSalesServiceManagerList();
    this.getContractBillingManagerList();
    this.getContractStateList();
  }

  public cancelFormsModification() {
    this.initForm();
    this.initContractForm();
  }

  public isFormValid(): boolean {
    return this.contractFormGroup.valid;
  }

  public getFormData() {
    return this.contractFormGroup.value;
  }

  public markAllAsTouched() {
    this.contractFormGroup.markAllAsTouched();
  }

  protected initForm() {
    this.contractFormGroup = this.fb.group({
      contractType: [null, Validators.required],
      salesServiceManager: [null],
      billingManager: [null],
      sentOn: [null, Validators.required],
      customerReturnDate: [null, Validators.required],
      contractState: [null, Validators.required],
    });

    // Initialisation du formulaire parent
    this.sentOnDateForm = this.fb.group({
      date: [null], // ce champ sera mis à jour par le DatePicker
    });

    // Initialisation du formulaire parent
    this.customerReturnDateForm = this.fb.group({
      date: [null], // ce champ sera mis à jour par le DatePicker
    });
  }

  private initContractForm() {
    if (
      !this.contractItemInformations.contractType &&
      !this.contractItemInformations.salesServiceManager &&
      !this.contractItemInformations.billingManager &&
      !this.contractItemInformations.state
    ) {
      return;
    }

    let contractType = this.contractItemInformations.contractType ? new ContractCgvType(this.contractItemInformations.contractType) : null;

    let salesServiceManager = this.contractItemInformations.salesServiceManager
      ? new ContractUt(this.contractItemInformations.salesServiceManager)
      : null;

    let billingManager = this.contractItemInformations.billingManager ? new ContractUt(this.contractItemInformations.billingManager) : null;

    let contractState = this.contractItemInformations.state ? new ContractState(this.contractItemInformations.state) : null;

    this.contractFormGroup.patchValue({
      contractType: contractType,
      salesServiceManager: salesServiceManager,
      billingManager: billingManager,
      sentOn: this.contractItemInformations.sentOn,
      customerReturnDate: this.contractItemInformations.customerReturnDate,
      contractState: contractState,
    });

    this.sentOnDate = this.contractItemInformations.sentOn;
    this.customerReturnDate = this.contractItemInformations.customerReturnDate;
    this.sentOnDateControl.setValue(this.sentOnDate);
    this.customerReturnDateControl.setValue(this.customerReturnDate);
  }

  get sentOnDateControl(): FormControl {
    return this.sentOnDateForm.get('date') as FormControl;
  }

  get customerReturnDateControl(): FormControl {
    return this.customerReturnDateForm.get('date') as FormControl;
  }

  // --- Load autocomplete data ---

  // --- contract type ---

  private getContractCgvType() {
    this.isContractCgvTypeLoading = true;

    this.contractCgvTypeService
      .getList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractCgvType => {
          this.isContractCgvTypeLoading = false;
          this.contractTypeList = contractCgvType;
          this.initFilteredContractTypeList();
        },
        error => {
          this.isContractCgvTypeLoading = false;
        }
      );
  }

  private initFilteredContractTypeList() {
    if (!this.isContractCgvTypeLoading) {
      this.filteredContractTypeList = this.contractFormGroup.get('contractType').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterContractType(label) : this.contractTypeList.slice()))
      );
    }
  }

  private _filterContractType(value: string = ''): ContractCgvType[] {
    const filterValue = value.toLowerCase();
    return this.contractTypeList.filter(
      contractTypeList =>
        contractTypeList.id.toString().toLowerCase().includes(filterValue) || contractTypeList.name.toLowerCase().includes(filterValue)
    );
  }

  // --- sales service manager & billing manager ---

  // sales service manager
  private getContractSalesServiceManagerList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'salesServiceManager' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;

          this.salesServiceManagerList = contractUtList;
          this.billingManagerList = contractUtList;

          this.initFilteredSalesServiceManagerList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredSalesServiceManagerList() {
    if (!this.isContractUtLoading) {
      this.filteredSalesServiceManagerList = this.contractFormGroup.get('salesServiceManager').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterSalesServiceManager(label) : this.salesServiceManagerList.slice()))
      );
    }
  }

  private _filterSalesServiceManager(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();
    return this.salesServiceManagerList.filter(
      salesServiceManager =>
        salesServiceManager.id.toString().toLowerCase().includes(filterValue) ||
        salesServiceManager.name.toLowerCase().includes(filterValue)
    );
  }

  // billing manager

  private getContractBillingManagerList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'billingManager' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;

          this.salesServiceManagerList = contractUtList;
          this.billingManagerList = contractUtList;

          this.initFilteredBillingManagerList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredBillingManagerList() {
    if (!this.isContractUtLoading) {
      this.filteredBillingManagerList = this.contractFormGroup.get('billingManager').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterBillingManager(label) : this.billingManagerList.slice()))
      );
    }
  }

  private _filterBillingManager(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();
    return this.billingManagerList.filter(
      billingManager =>
        billingManager.id.toString().toLowerCase().includes(filterValue) || billingManager.name.toLowerCase().includes(filterValue)
    );
  }

  // --- contract state ---

  public getContractStateList(newContractStateId: number = null) {
    this.isContractStateLoading = true;

    this.contractStateService
      .getList({
        year: new Date().getFullYear(),
        stateIdGreaterThan: newContractStateId ? newContractStateId : this.contractItemInformations?.state.id,
      })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractState => {
          this.isContractStateLoading = false;
          this.contractStateList = contractState;
          this.initFilteredContractStateList();
        },
        error => {
          this.isContractStateLoading = false;
        }
      );
  }

  private initFilteredContractStateList() {
    if (!this.isContractStateLoading) {
      this.filteredContractStateList = this.contractFormGroup.get('contractState').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterContractState(label) : this.contractStateList.slice()))
      );
    }
  }

  private _filterContractState(value: string = ''): ContractState[] {
    const filterValue = value.toLowerCase();
    return this.contractStateList.filter(
      contractState =>
        contractState.id.toString().toLowerCase().includes(filterValue) || contractState.name.toLowerCase().includes(filterValue)
    );
  }

  // --- Utility ---

  private _getLabel(value: string | ContractCgvStatus | ContractSector): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public displayAutoCompleteData(data: any): string {
    if (!data) return '';
    return typeof data === 'string' ? data : data.name + ' (' + data.id + ')';
  }

  onSentOnDateChosen(): void {
    const selectedDate = this.sentOnDateForm.get('date').value;
    this.contractFormGroup.get('sentOn').setValue(selectedDate);
  }

  onCustomerReturnDateChosen(): void {
    const selectedDate = this.customerReturnDateForm.get('date').value;
    this.contractFormGroup.get('customerReturnDate').setValue(selectedDate);
  }

  // --- On Destroy ---

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
